import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../../../Constant';
import { getToken, getAuthHeaders, hasAuthSession } from '../../../../utils/authStorage';
import { showLoading } from 'react-global-loading';
import { toast, ToastContainer } from 'react-toastify';
import messages from '../../../../assets/Messages.json';
import 'react-toastify/dist/ReactToastify.css';
import HeaderInfo from '../../../../Components/Common/HeaderInfo';
import useASIItemMaster from '../../../../hooks/useASIItemMaster';
import Remarks, { validateRemarksSection } from './Remarks';
import Remarks__list from './Remarks__list';
import { markPageAsSaved, PAGE_NAMES, refreshBalanceSheetValidation } from '../../../../Components/BalanceSheet/BalanceSheetHeader';
import { filterRemarksList } from '../../../../Custom/Custom';
import { confirmAndDelete } from '../../../../utils/confirmAndDelete';

const getAuthConfig = () => {
  if (!hasAuthSession()) return null;
  return { ...getAuthHeaders(), headers: { ...getAuthHeaders().headers, 'Cache-Control': 'no-cache' } };
};

function InputItemsImported() {
  const navigate = useNavigate();
  const dtableRef = useRef(null);

  // Use the custom hook for ASI Item Master data
  const {
    itemMasterData,
    filteredItems,
    isLoading: loadingItemMaster,
    debouncedSearch,
    loadMoreData,
    hasMoreData,
    isLoadingMore,
    // fetchUnitByItemCode
  } = useASIItemMaster();

  // State for header data
  const [headerData, setHeaderData] = useState({
    despatchSlNo: '',
    permanentSlNo: '',
    companyName: '',
    dslNo: '',
    surveyYear: ''
  });

  const [itemData, setItemData] = useState({});
  const [checkedItems, setCheckedItems] = useState([]);
  const [buttonStatus, setButtonStatus] = useState('');
  const [originalValues, setOriginalValues] = useState({}); 
  const [validationErrors, setValidationErrors] = useState({}); 

  // State for Remarks section
  const [remarksData, setRemarksData] = useState({
    remarksBlock: '',
    remarksItems: '',
    remarks: ''
  });
  const [remarksList, setRemarksList] = useState([]);

  const [totalProfitandLossAmount, setTotalProfitandLossAmount] = useState({
    SL_No: '',
    Description: '',
    Amount: ''
  });
  const [profitLossAmountDirty, setProfitLossAmountDirty] = useState(false);
  const [profitLossAmountError, setProfitLossAmountError] = useState('');

  // State for managing dropdown visibility for each row and field
  const [dropdownStates, setDropdownStates] = useState({});
  const [selectedIndex, setSelectedIndex] = useState({}); 
  const dropdownItemRefs = useRef({}); 
  const justOpenedRef = useRef({}); 

  const selectedItemCodes = useMemo(() => {
    const selected = {};
    for (let i = 0; i < 6; i++) {
      const item = itemData[i];
      if (item && item.itemCode && item.itemCode.toString().trim() !== '') {
        selected[i] = item.itemCode.toString().trim();
      }
    }
    return selected;
  }, [itemData]);

  // Function to get filtered items for a specific row (excluding already selected items in other rows)
  const getFilteredItemsForRow = useCallback((rowIndex, items) => {
    return items.filter(dropdownItem => {
      const dropdownItemCode = dropdownItem.ASICC_CODE?.toString().trim();
      // Check if this item is selected in any other row (not the current row)
      for (const [index, code] of Object.entries(selectedItemCodes)) {
        if (parseInt(index) !== rowIndex && code === dropdownItemCode) {
          return false;
        }
      }
      return true;
    });
  }, [selectedItemCodes]);


  const selectedItemDescriptions = useMemo(() => {
    const descriptions = [];

    // Add user selected items from first 6 rows
    for (let i = 0; i < 6; i++) {
      const item = itemData[i];
      if (item && item.itemDesc && item.itemDesc.toString().trim() !== '') {
        descriptions.push(item.itemDesc.toString().trim());
      }
    }

    // Add row 7 (Other Imported Items) - should always be shown
    const row7Item = itemData[6];
    if (row7Item && row7Item.itemDesc && row7Item.itemDesc.toString().trim() !== '') {
      descriptions.push(row7Item.itemDesc.toString().trim());
    }

    return descriptions;
  }, [itemData]);

  // Total Imports (consumed) (items 1 to 6) – sum of rows 1–6 (indices 0–5) for Quantity Consumed & Purchase Value
  const totalImportsQtyConsumed = useMemo(() => {
    let sum = 0;
    for (let i = 0; i < 6; i++) {
      const item = itemData[i];
      if (item?.qtyConsumed !== undefined && item?.qtyConsumed !== '') {
        const val = parseFloat(item.qtyConsumed);
        if (!Number.isNaN(val)) sum += val;
      }
    }
    return sum;
  }, [itemData]);

  const totalImportsPurchaseValue = useMemo(() => {
    let sum = 0;
    for (let i = 0; i < 6; i++) {
      const item = itemData[i];
      if (item?.purchaseValue !== undefined && item?.purchaseValue !== '') {
        const val = parseFloat(item.purchaseValue);
        if (!Number.isNaN(val)) sum += val;
      }
    }
    return sum;
  }, [itemData]);

  // Auto-fill Profit & Loss Amount from Total Imports Purchase Value
  useEffect(() => {
    if (profitLossAmountDirty) return;
    setTotalProfitandLossAmount(prev => ({
      ...prev,
      Amount: totalImportsPurchaseValue ? String(totalImportsPurchaseValue) : ''
    }));
    setProfitLossAmountError('');
  }, [totalImportsPurchaseValue, profitLossAmountDirty]);

  // Function to toggle dropdown for specific row and field
  const toggleDropdown = (rowIndex, fieldType) => {
    const key = `${rowIndex}-${fieldType}`;
    setDropdownStates(prev => {
      const newState = {};
      // Close all other dropdowns and toggle current one
      const isCurrentlyOpen = prev[key] || false;
      newState[key] = !isCurrentlyOpen;
      return newState;
    });
    // Reset selected index when opening dropdown
    setSelectedIndex(prev => {
      const currentState = prev[key];
      if (currentState === undefined || currentState >= 0) {
        return { ...prev, [key]: -1 };
      }
      return prev;
    });
  };

  // Function to open dropdown (without toggle)
  const openDropdown = (rowIndex, fieldType) => {
    const key = `${rowIndex}-${fieldType}`;
    setDropdownStates(prev => {
      // If already open, don't do anything
      if (prev[key]) {
        return prev;
      }
      const newState = {};
      newState[key] = true;
      justOpenedRef.current[key] = true;
      setTimeout(() => {
        justOpenedRef.current[key] = false;
      }, 300);
      return newState;
    });
    setSelectedIndex(prev => ({ ...prev, [key]: -1 }));
  };

  const closeDropdown = (rowIndex, fieldType) => {
    const key = `${rowIndex}-${fieldType}`;
    setDropdownStates(prev => ({
      ...prev,
      [key]: false
    }));
    // Reset selected index when closing dropdown
    setSelectedIndex(prev => ({ ...prev, [key]: -1 }));
  };

  const closeAllDropdowns = () => {
    setDropdownStates({});
    setSelectedIndex({});
  };

  const dataLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);
  const apiCallMadeRef = useRef(false);

  // Function to fetch existing data from API
  const fetchExistingData = useCallback(async (dslNo, surveyYear) => {
    if (!dslNo || !surveyYear) {
      console.log('DSL No or Survey Year not available for fetching data');
      dataLoadedRef.current = true;
      return;
    }

    console.log('Starting API call for getInputIItems with DSL:', dslNo, 'Year:', surveyYear);
    showLoading(true);
    try {
      const currentConfig = getAuthConfig();
      if (!currentConfig) {
        console.error('Authentication token not found for fetching data');
        showLoading(false);
        dataLoadedRef.current = true;
        return;
      }

      // Extract only year part (e.g., "2024" from "2024-25")
      const yearOnly = surveyYear ? surveyYear.split('-')[0] : surveyYear;

      const payload = {
        dsl_no: dslNo,
        survey_year: yearOnly
      };

      console.log('Fetching existing data with payload:', payload);

      const response = await axios.post(`${API_URL}api/input-items-directlyItems/getInputIItems`, payload, currentConfig);

      if (response.data && response.data.status && response.data.data) {
        console.log('Data fetched successfully:', response.data.data);

        // Map API data to form data
        const apiData = response.data.data;
        const mappedData = {};

        // Find "Other Imported Items" from API for row 7 – by item_desc first, else by item_code 9922100 (so 9922100 never goes to upper rows)
        const otherImportedItem = apiData.find(item => (item.item_desc || item.itemDesc || '') === 'Other Imported Items')
          || apiData.find(item => String(item.item_code ?? '') === '9922100');
        const otherPurchaseValue = otherImportedItem != null ? String(otherImportedItem.purchase_value ?? '') : '';
        const otherQtyConsumed = otherImportedItem != null ? String(otherImportedItem.qty_consumed ?? '') : '';
        const otherRatePerUnit = otherImportedItem != null ? String(otherImportedItem.rate_per_unit ?? '') : '';
        // When API sends item_code "" or "9922100" for Other Imported Items: Item Description empty (no selected)
        const otherItemCodeFromApi = otherImportedItem != null ? String(otherImportedItem.item_code ?? '').trim() : '';
        const otherItemDescForRow = (otherItemCodeFromApi === '' || otherItemCodeFromApi === '9922100') ? '' : 'Other Imported Items';

        // Find "Total Imports (consumed)" from API for row 8 – by item_code 9994000
        const totalImportsItem = apiData.find(item => String(item.item_code ?? '') === '9994000');
        const totalImportsQty = totalImportsItem != null ? String(totalImportsItem.qty_consumed ?? '') : '';
        const totalImportsPurchaseValue = totalImportsItem != null ? String(totalImportsItem.purchase_value ?? '') : '';
        const totalImportsRatePerUnit = totalImportsItem != null ? String(totalImportsItem.rate_per_unit ?? '') : '';
        const totalImportsUnitOfQty = totalImportsItem != null ? String(totalImportsItem.unit_of_qty ?? '') : '';

        // Process each item from API - skip "Other Imported Items", item_code 9922100 and 9994000 (they belong in row 7 & 8 only)
        let regularItemIndex = 0;
        apiData.forEach((item, index) => {
          if ((item.item_desc || item.itemDesc || '') === "Other Imported Items") {
            return;
          }
          if (String(item.item_code ?? '') === '9922100') {
            return;
          }
          if (String(item.item_code ?? '') === '9994000') {
            return;
          }

          if (regularItemIndex < 6) {
            mappedData[regularItemIndex] = {
              id: regularItemIndex,
              dslNo: item.dsl_no?.toString() || '',
              surveyYear: item.survey_year?.toString() || '',
              itemDesc: item.item_desc || '',
              itemCode: item.item_code?.toString() || '',
              unitOfQty: item.unit_of_qty || '',
              qtyConsumed: item.qty_consumed?.toString() || '',
              purchaseValue: item.purchase_value?.toString() || '',
              ratePerUnit: item.rate_per_unit?.toString() || '',
              statusFlag: '',
              saveOrderBy: regularItemIndex + 1,
              itemCodeClass: 'textBox_back_color_white'
            };
            regularItemIndex++;
          }
        });

        // Fill remaining rows with empty data if less than 6 items (first 6 rows are editable)
        for (let i = regularItemIndex; i < 6; i++) {
          mappedData[i] = {
            id: i,
            dslNo: dslNo,
            surveyYear: surveyYear,
            itemDesc: '',
            itemCode: '',
            unitOfQty: '',
            qtyConsumed: '',
            purchaseValue: '',
            ratePerUnit: '',
            statusFlag: '',
            saveOrderBy: i + 1,
            itemCodeClass: 'textBox_back_color_white'
          };
        }

        // Set row 7 (index 6) - Other Imported Items; map purchase_value (and qty, rate) from API; when item_code "" or 9922100, Item Description empty
        mappedData[6] = {
          id: 6,
          dslNo: dslNo,
          surveyYear: surveyYear,
          itemDesc: 'Other Imported Items',
          itemCode: '9922100',
          unitOfQty: '',
          qtyConsumed: otherQtyConsumed,
          purchaseValue: otherPurchaseValue,
          ratePerUnit: otherRatePerUnit,
          statusFlag: '',
          saveOrderBy: 7,
          itemCodeClass: 'textBox_back_color_white'
        };

        // Set row 8 (index 7) - Total Imports (consumed) (items 1 to 6); NPC-MS 9994000; map data from API when item_code 9994000 present
        mappedData[7] = {
          id: 7,
          dslNo: dslNo,
          surveyYear: surveyYear,
          itemDesc: 'Total Imports (consumed) (items 1 to 6)',
          itemCode: '9994000',
          unitOfQty: totalImportsUnitOfQty,
          qtyConsumed: totalImportsQty,
          purchaseValue: totalImportsPurchaseValue,
          ratePerUnit: totalImportsRatePerUnit,
          statusFlag: '',
          saveOrderBy: 8,
          itemCodeClass: 'textBox_back_color_white'
        };

        setItemData(mappedData);

        // Map remarks data from API response (similar to InputItemsIndigenous.jsx)
        let mappedRemarksList = [];
        if (response.data.remarks && Array.isArray(response.data.remarks)) {
          mappedRemarksList = response.data.remarks.map((remark, index) => {
            // Extract block letter from "Block I" format (e.g., "Block I" -> "I")
            let blockLetter = '';
            if (remark.block) {
              blockLetter = remark.block.replace(/^Block\s+/i, '').trim();
            }

            return {
              slNo: index + 1,
              blocks: blockLetter || remark.block || '',
              columns: remark.columns_name || remark.columnsName || '',
              items: remark.items || '',
              remarks: remark.remarks || '',
              remarksCreatedOn: remark.created_date || remark.createdDate || remark.updated_date || '',
              remarksBy: remark.updated_by || remark.updatedBy || remark.created_by || '',
              role: remark.updated_by || remark.updatedBy || ''
            };
          });
          setRemarksList(mappedRemarksList);
        } else {
          setRemarksList([]);
        }

        // Set default remarksData - Block to "ALL" by default to show all remarks
        setRemarksData({
          remarksBlock: '',
          remarksItems: '',
          remarks: ''
        });

        // Map totalProfitandLossAmount from API response
        if (response.data.totalProfitandLossAmount) {
          const profitLossData = response.data.totalProfitandLossAmount;
          setTotalProfitandLossAmount({
            SL_No: profitLossData.SL_No !== undefined ? profitLossData.SL_No : (profitLossData.sl_No !== undefined ? profitLossData.sl_No : ''),
            Description: profitLossData.Description || profitLossData.description || '',
            Amount: profitLossData.Amount !== undefined && profitLossData.Amount !== null ? profitLossData.Amount.toString() : (profitLossData.amount !== undefined && profitLossData.amount !== null ? profitLossData.amount.toString() : '')
          });
        } else {
          // Reset to empty if not available
          setTotalProfitandLossAmount({
            SL_No: '',
            Description: '',
            Amount: ''
          });
        }

        dataLoadedRef.current = true; // Mark data as loaded
        console.log('API data loaded successfully, dataLoadedRef set to true');
        console.log('Remarks loaded:', mappedRemarksList);
      } else {
        console.log('No data found or invalid response');
        initializeTableData();
        // Reset totalProfitandLossAmount if no data
        setTotalProfitandLossAmount({
          SL_No: '',
          Description: '',
          Amount: ''
        });
        dataLoadedRef.current = true; // Mark data as loaded even if no data found
        console.log('No data found, initialized with default data, dataLoadedRef set to true');
      }
    } catch (error) {
      console.error('Error fetching data from API:', error);
      console.error('Error Response:', error.response);
      console.error('Error Status:', error.response?.status);
      console.error('Error Data:', error.response?.data);

      if (error.response?.status === 404) {
        console.log('No existing data found, initializing with default data');
        initializeTableData();
      } else {
        toast.error('Failed to load existing data. Using default form.', messages.COLOR_RED);
        initializeTableData();
      }
      dataLoadedRef.current = true; // Mark data as loaded even on error
      console.log('Error occurred, initialized with default data, dataLoadedRef set to true');
    } finally {
      showLoading(false);
    }
  }, []); // Empty dependency array since function doesn't depend on any props or state

  // Memoize the fetch function to prevent recreation
  const fetchHeaderData = useCallback(async () => {
    // Prevent multiple API calls using refs
    if (dataLoadedRef.current || isLoadingRef.current || apiCallMadeRef.current) {
      console.log('Data already loaded, loading in progress, or API call already made - skipping');
      return;
    }

    try {
      const balanceSheetData = localStorage.getItem('balanceSheetFormData');

      if (balanceSheetData) {
        const parsedData = JSON.parse(balanceSheetData);

        const headerInfo = {
          despatchSlNo: parsedData.dslNo || '',
          permanentSlNo: parsedData.pslNo || '',
          companyName: parsedData.nameUnit || '',
          dslNo: parsedData.dslNo || '',
          surveyYear: parsedData.year || ''
        };

        setHeaderData(headerInfo);

        // Fetch existing data if DSL No and Survey Year are available
        if (headerInfo.dslNo && headerInfo.surveyYear) {
          apiCallMadeRef.current = true; // Mark API call as made
          isLoadingRef.current = true;
          await fetchExistingData(headerInfo.dslNo, headerInfo.surveyYear);
          isLoadingRef.current = false;
        } else {
          initializeTableData();
        }
      } else {
        console.log('No balanceSheet data found in localStorage');
        setHeaderData({
          despatchSlNo: 'DSL No. not set',
          permanentSlNo: 'PSL No. not set',
          companyName: 'Company name not set',
          dslNo: '',
          surveyYear: ''
        });
        initializeTableData();
      }
    } catch (error) {
      console.error('Error fetching data from localStorage:', error);
      setHeaderData({
        despatchSlNo: 'Error loading data',
        permanentSlNo: 'Error loading data',
        companyName: 'Error loading data',
        dslNo: '',
        surveyYear: ''
      });
      initializeTableData();
    }
  }, []);

  // Fetch header data from localStorage when component mounts
  useEffect(() => {
    fetchHeaderData();
  }, [fetchHeaderData]); // Only depend on the memoized function

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const clickedElement = event.target;

      // Check if click is inside any dropdown container
      const clickedDropdownContainer = clickedElement.closest('.position-relative');

      // Check if click is on dropdown menu items
      const isDropdownMenu = clickedElement.closest('.position-absolute.bg-white.border.rounded.shadow-sm');

      // Check if click is on an input field
      const isInputField = clickedElement.tagName === 'INPUT' && clickedElement.type === 'text';

      // Find all open dropdowns
      const openDropdowns = Object.keys(dropdownStates).filter(key => dropdownStates[key]);

      if (openDropdowns.length > 0) {
        // If click is on an input field
        if (isInputField) {
          // Check if the clicked input container has an open dropdown visible
          let isInsideOpenDropdown = false;

          if (clickedDropdownContainer) {
            // Check if this container has a visible dropdown menu
            const visibleDropdown = clickedDropdownContainer.querySelector('.position-absolute.bg-white.border.rounded.shadow-sm');
            if (visibleDropdown && visibleDropdown.offsetParent !== null) {
              // Dropdown is visible in this container, so don't close
              isInsideOpenDropdown = true;
            }
          }

          // If clicked input is not inside any open dropdown container, close all dropdowns
          if (!isInsideOpenDropdown) {
            closeAllDropdowns();
          }
        } else if (!clickedDropdownContainer && !isDropdownMenu) {
          // If click is completely outside any dropdown container and not on dropdown menu, close all
          closeAllDropdowns();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownStates]);

  // Initialize table with default data
  const initializeTableData = () => {
    const initialData = {};

    // First 6 rows (user editable)
    for (let i = 0; i < 6; i++) {
      initialData[i] = {
        id: i,
        dslNo: headerData.despatchSlNo,
        surveyYear: '2023-24',
        itemDesc: '',
        itemCode: '',
        unitOfQty: '',
        qtyConsumed: '',
        purchaseValue: '',
        ratePerUnit: '',
        statusFlag: '',
        saveOrderBy: 1,
        itemCodeClass: 'textBox_back_color_white'
      };
    }

    // Set specific data for row 7 (index 6) - Other Imported Items
    initialData[6] = {
      id: 6,
      dslNo: headerData.despatchSlNo,
      surveyYear: '2023-24',
      itemDesc: 'Other Imported Items',
      itemCode: '9922100',
      unitOfQty: '',
      qtyConsumed: '',
      purchaseValue: '',
      ratePerUnit: '',
      statusFlag: '',
      saveOrderBy: 7,
      itemCodeClass: 'textBox_back_color_white'
    };

    // Set specific data for row 8 (index 7) - Values from PL Account for information
    initialData[7] = {
      id: 7,
      dslNo: headerData.despatchSlNo,
      surveyYear: '2023-24',
      itemDesc: 'Total Imports (consumed) (items 1 to 6)',
      itemCode: '9994000',
      unitOfQty: '',
      qtyConsumed: '',
      purchaseValue: '0',
      ratePerUnit: '',
      statusFlag: '',
      saveOrderBy: 8,
      itemCodeClass: 'textBox_back_color_white'
    };

    setItemData(initialData);
    dataLoadedRef.current = true; // Mark data as loaded
    console.log('Table data initialized, dataLoadedRef set to true');
  };

  // Industry standard validation functions
  const validateNumericInput = (value, fieldName, maxLength = null, allowDecimal = true, allowNegative = false) => {
    // Remove any non-numeric characters except decimal point and minus sign
    let sanitized = value;

    if (allowDecimal) {
      // Allow digits, single decimal point, and optionally minus sign
      sanitized = value.replace(/[^\d.-]/g, '');
      // Ensure only one decimal point
      const parts = sanitized.split('.');
      if (parts.length > 2) {
        sanitized = parts[0] + '.' + parts.slice(1).join('');
      }
      // Ensure minus sign only at the start
      if (!allowNegative) {
        sanitized = sanitized.replace(/-/g, '');
      } else {
        sanitized = sanitized.replace(/^-?/, sanitized.startsWith('-') ? '-' : '');
        sanitized = sanitized.replace(/(?!^)-/g, '');
      }
    } else {
      // Only digits, optionally minus sign
      if (allowNegative) {
        sanitized = value.replace(/[^\d-]/g, '');
        sanitized = sanitized.replace(/^-?/, sanitized.startsWith('-') ? '-' : '');
        sanitized = sanitized.replace(/(?!^)-/g, '');
      } else {
        sanitized = value.replace(/[^\d]/g, '');
      }
    }

    // Apply max length if specified
    if (maxLength && sanitized.length > maxLength) {
      sanitized = sanitized.substring(0, maxLength);
    }

    return sanitized;
  };

  const validateItemCode = (value) => {
    // Item code should be exactly 7 digits
    const sanitized = value.replace(/[^\d]/g, '');
    return sanitized.substring(0, 7);
  };

  const validateRequired = (value, fieldName) => {
    if (!value || value.trim() === '') {
      return `${fieldName} is required`;
    }
    return null;
  };

  const validateNumberRange = (value, min = null, max = null) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return null; // Empty is allowed, will be checked by required validation
    }
    if (min !== null && numValue < min) {
      return `Value must be at least ${min}`;
    }
    if (max !== null && numValue > max) {
      return `Value must not exceed ${max}`;
    }
    return null;
  };

  const handleInputChange = (index, field, value) => {
    const newFormData = { ...itemData };
    const newErrors = { ...validationErrors };
    let sanitizedValue = value;
    let error = null;

    // Apply field-specific validation
    switch (field) {
      case 'itemCode':
        sanitizedValue = validateItemCode(value);
        if (sanitizedValue.length > 0 && sanitizedValue.length < 7) {
          error = 'Item Code must be 7 digits';
        }
        break;

      case 'qtyConsumed':
        sanitizedValue = validateNumericInput(value, 'Quantity Consumed', 18, true, false);
        if (sanitizedValue) {
          error = validateNumberRange(sanitizedValue, 0, null);
        }
        break;

      case 'purchaseValue':
        sanitizedValue = validateNumericInput(value, 'Purchase Value', 12, true, false);
        if (sanitizedValue) {
          error = validateNumberRange(sanitizedValue, 0, null);
        }
        break;

      case 'ratePerUnit':
        sanitizedValue = validateNumericInput(value, 'Rate Per Unit', 18, true, false);
        if (sanitizedValue) {
          error = validateNumberRange(sanitizedValue, 0, null);
        }
        break;

      case 'itemDesc':
        // Text field - allow alphanumeric and special characters, limit length
        sanitizedValue = value.substring(0, 200);
        break;

      case 'unitOfQty':
        // Text field - limit length
        sanitizedValue = value.substring(0, 19);
        break;

      default:
        sanitizedValue = value;
    }

    // Update form data
    newFormData[index][field] = sanitizedValue;

    // Recalculate Rate Per Unit = Purchase Value / Quantity Consumed when either changes
    if (field === 'purchaseValue' || field === 'qtyConsumed') {
      const pv = newFormData[index].purchaseValue;
      const qty = newFormData[index].qtyConsumed;
      const pvNum = pv !== '' && pv != null ? parseFloat(pv) : NaN;
      const qtyNum = qty !== '' && qty != null ? parseFloat(qty) : NaN;
      if (Number.isFinite(pvNum) && Number.isFinite(qtyNum) && qtyNum > 0) {
        newFormData[index].ratePerUnit = (pvNum / qtyNum).toFixed(2);
      } else {
        newFormData[index].ratePerUnit = '';
      }
    }

    setItemData(newFormData);

    // Update validation errors
    if (error) {
      newErrors[`${index}-${field}`] = error;
    } else {
      delete newErrors[`${index}-${field}`];
    }
    setValidationErrors(newErrors);
  };

  // Handle dropdown change for Item Description - using API data
  const handleItemDescriptionChange = (index, value) => {
    const newFormData = { ...itemData };

    if (value) {
      const selectedItem = itemMasterData.find(item => item.ITEM_NAME === value);
      if (selectedItem) {
        newFormData[index] = {
          ...newFormData[index],
          itemDesc: selectedItem.ITEM_NAME,
          itemCode: selectedItem.ASICC_CODE,
          unitOfQty: selectedItem.UNIT_NAME || ''
        };
      }
    } else {
      newFormData[index] = {
        ...newFormData[index],
        itemDesc: '',
        itemCode: '',
        unitOfQty: ''
      };
    }

    setItemData(newFormData);
    closeDropdown(index, 'itemDesc');
  };

  // Handle dropdown change for Item Code - using API data
  const handleItemCodeChange = async (index, value) => {
    const newFormData = { ...itemData };

    if (value) {
      const selectedItem = itemMasterData.find(item => item.ASICC_CODE === value);
      if (selectedItem) {
        newFormData[index] = {
          ...newFormData[index],
          itemDesc: selectedItem.ITEM_NAME,
          itemCode: selectedItem.ASICC_CODE,
          unitOfQty: selectedItem.UNIT_NAME || ''
        };
      }
    } else {
      newFormData[index] = {
        ...newFormData[index],
        itemDesc: '',
        itemCode: '',
        unitOfQty: ''
      };
    }

    setItemData(newFormData);
    closeDropdown(index, 'itemCode');
  };

  // Handle keyboard navigation in dropdown
  const handleKeyDown = (e, rowIndex, fieldType, filteredItemsList) => {
    const key = `${rowIndex}-${fieldType}`;
    const currentSelected = selectedIndex[key] !== undefined ? selectedIndex[key] : -1;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        // Open dropdown if closed
        if (!dropdownStates[key]) {
          openDropdown(rowIndex, fieldType);
        }
        // Move selection down
        if (currentSelected < filteredItemsList.length - 1) {
          const newIndex = currentSelected + 1;
          setSelectedIndex(prev => ({ ...prev, [key]: newIndex }));
          // Scroll into view
          setTimeout(() => {
            const itemRef = dropdownItemRefs.current[`${key}-${newIndex}`];
            if (itemRef) {
              itemRef.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
        }
        break;

      case 'ArrowUp':
        e.preventDefault();
        // Open dropdown if closed
        if (!dropdownStates[key]) {
          openDropdown(rowIndex, fieldType);
        }
        // Move selection up
        if (currentSelected > 0) {
          const newIndex = currentSelected - 1;
          setSelectedIndex(prev => ({ ...prev, [key]: newIndex }));
          // Scroll into view
          setTimeout(() => {
            const itemRef = dropdownItemRefs.current[`${key}-${newIndex}`];
            if (itemRef) {
              itemRef.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
            }
          }, 0);
        }
        break;

      case 'Enter':
        e.preventDefault();
        if (dropdownStates[key] && currentSelected >= 0 && currentSelected < filteredItemsList.length) {
          // Select the highlighted item
          if (fieldType === 'itemDesc') {
            handleItemDescriptionChange(rowIndex, filteredItemsList[currentSelected].ITEM_NAME);
          } else if (fieldType === 'itemCode') {
            handleItemCodeChange(rowIndex, filteredItemsList[currentSelected].ASICC_CODE);
          }
        } else if (!dropdownStates[key]) {
          // Open dropdown if closed
          openDropdown(rowIndex, fieldType);
        }
        break;

      case 'Escape':
        e.preventDefault();
        // Close dropdown
        closeDropdown(rowIndex, fieldType);
        break;

      case 'Tab':
        // Close dropdown on tab
        closeDropdown(rowIndex, fieldType);
        break;

      default:
        // For other keys, open dropdown if typing
        if (!dropdownStates[key] && e.key.length === 1) {
          openDropdown(rowIndex, fieldType);
        }
        break;
    }
  };

  // Handle search input change
  const handleSearchChange = (value) => {
    debouncedSearch(value);
  };

  // Handle reset function to clear all fields
  const handleReset = () => {
    const newItemData = { ...itemData };
    Object.keys(newItemData).forEach(key => {
      newItemData[key] = {
        ...newItemData[key],
        itemDesc: '',
        itemCode: '',
        unitOfQty: '',
        qtyConsumed: '',
        purchaseValue: ''
      };
    });
    setItemData(newItemData);
    setCheckedItems([]);
    closeAllDropdowns();
  };

  // Clear row at index (blank row) – delete only clears row; rows 7 & 8 stay fixed
  const clearRowAtState = (index) => {
    const blankRow = {
      id: index,
      dslNo: headerData.despatchSlNo,
      surveyYear: headerData.surveyYear || '2023-24',
      itemDesc: '',
      itemCode: '',
      unitOfQty: '',
      qtyConsumed: '',
      purchaseValue: '',
      ratePerUnit: '',
      statusFlag: '',
      saveOrderBy: index + 1,
      itemCodeClass: 'textBox_back_color_white'
    };
    setItemData(prev => ({ ...prev, [index]: blankRow }));
  };

  // Delete single row: confirm → call delete API (api/balanceSheet/delete-item) → on success clear row
  // Only first 6 rows are deletable (rows 7 & 8 are system: Other Imported Items, Total Imports)
  const handleDeleteSingleRow = async (index) => {
    if (index >= 6) return;
    const item = itemData[index];
    const itemDesc = item?.itemDesc || '';
    const itemCode = item?.itemCode || '';
    const message = `Are you sure you want to delete Row ${index + 1}?${itemDesc ? `\n\nItem: ${itemDesc}` : ''}${itemCode ? `\nItem Code: ${itemCode}` : ''}`;

    const surveyYearOnly = (headerData.surveyYear || '').split('-')[0] || '';
    const qtyConsumed = item?.qtyConsumed !== undefined && item?.qtyConsumed !== '' ? parseFloat(item.qtyConsumed) || 0 : 0;
    const purchaseValue = item?.purchaseValue !== undefined && item?.purchaseValue !== '' ? parseFloat(item.purchaseValue) || 0 : 0;

    const deleted = await confirmAndDelete({
      message,
      showLoadingDuringCall: true,
      deleteApiCall: async () => {
        const payload = {
          dsl_no: headerData.despatchSlNo || headerData.dslNo || '',
          survey_year: surveyYearOnly,
          block_type: 'I',
          block_title: 'Input Items Imported',
          row_index: index,
          item_desc: itemDesc,
          item_code: (itemCode || '').toString().trim(),
          qty_consumed: qtyConsumed,
          purchase_value: purchaseValue
        };
        return axios.post(`${API_URL}api/balanceSheet/delete-item`, payload, getAuthConfig());
      },
      onSuccess: () => clearRowAtState(index),
    });

    if (!deleted) return;
  };

  // Function to validate all form data before saving
  const validateFormData = () => {
    const errors = [];

    for (let i = 0; i < 8; i++) {
      const item = itemData[i];
      if (item && (item.itemDesc || item.itemCode || item.qtyConsumed || item.purchaseValue)) {
        // Validate Item Code if provided (skip for row 6 and 7 which are special rows)
        if (i < 6 && item.itemCode && item.itemCode.length > 0 && item.itemCode.length !== 7) {
          errors.push(`Row ${i + 1}: Item Code must be exactly 7 digits`);
        }

        // Validate Quantity Consumed
        if (item.qtyConsumed && i !== 6 && i !== 7) {
          const qty = parseFloat(item.qtyConsumed);
          if (isNaN(qty) || qty < 0) {
            errors.push(`Row ${i + 1}: Quantity Consumed must be a positive number`);
          }
        }

        // Validate Purchase Value
        if (item.purchaseValue && i !== 7) {
          const value = parseFloat(item.purchaseValue);
          if (isNaN(value) || value < 0) {
            errors.push(`Row ${i + 1}: Purchase Value must be a positive number`);
          }
        }

        // If item code is provided, item description should also be provided (for first 6 rows)
        if (i < 6 && item.itemCode && !item.itemDesc) {
          errors.push(`Row ${i + 1}: Item Description is required when Item Code is provided`);
        }
      }
    }

    return errors;
  };

  // Function to save data to API
  const handleSaveToAPI = async () => {
    if (!headerData.dslNo || !headerData.surveyYear) {
      toast.error('DSL No. and Survey Year are required to save data', messages.COLOR_RED);
      return false;
    }

    const remarksValidation = validateRemarksSection(remarksData);
    if (!remarksValidation.valid) {
      toast.error(remarksValidation.message, messages.COLOR_RED);
      return false;
    }

    // Validate form data before saving
    const validationErrors = validateFormData();
    if (validationErrors.length > 0) {
      toast.error(`Please fix the following errors:\n${validationErrors.join('\n')}`, messages.COLOR_RED);
      return false;
    }

    // Validate Profit & Loss Amount (user editable)
    const amountStr = (totalProfitandLossAmount.Amount ?? '').toString().trim();
    if (amountStr !== '') {
      if (amountStr.length > 12) {
        const msg = 'Amount must be at most 12 characters';
        setProfitLossAmountError(msg);
        toast.error(msg, messages.COLOR_RED);
        return false;
      }
      if (!/^\d*\.?\d*$/.test(amountStr)) {
        const msg = 'Only numbers allowed in Amount';
        setProfitLossAmountError(msg);
        toast.error(msg, messages.COLOR_RED);
        return false;
      }
      const amountNum = parseFloat(amountStr);
      if (!Number.isFinite(amountNum) || amountNum < 0) {
        const msg = 'Amount must be a non-negative number';
        setProfitLossAmountError(msg);
        toast.error(msg, messages.COLOR_RED);
        return false;
      }
      if (profitLossAmountDirty && totalImportsPurchaseValue) {
        const expected = Number(totalImportsPurchaseValue);
        if (Number.isFinite(expected) && Math.abs(amountNum - expected) > 0.01) {
          const msg = `Amount should match Total Imports Purchase Value (${expected})`;
          setProfitLossAmountError(msg);
          toast.error(msg, messages.COLOR_RED);
          return false;
        }
      }
    } else if (!profitLossAmountDirty && totalImportsPurchaseValue) {
      setProfitLossAmountError('');
    }

    showLoading(true);

    try {
      const currentConfig = getAuthConfig();
      if (!currentConfig) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        showLoading(false);
        return false;
      }

      // Extract year only from survey year (e.g., "2024-25" -> "2024")
      const surveyYearOnly = headerData.surveyYear.split('-')[0] || '2024';

      // Prepare data for API - only first 6 rows (user editable) + row 7 (Other Imported Items)
      const dataToSave = [];

      for (let i = 0; i < 7; i++) {
        const item = itemData[i];
        if (item && (item.itemDesc || item.itemCode || item.qtyConsumed || item.purchaseValue)) {
          // Sanitize numeric values
          const qtyConsumed = item.qtyConsumed ? parseFloat(item.qtyConsumed) || 0 : 0;
          const purchaseValue = item.purchaseValue ? parseFloat(item.purchaseValue) || 0 : 0;

          // Validate numeric values are positive (skip validation for row 7 which is special row)
          if (i < 6 && (qtyConsumed < 0 || purchaseValue < 0)) {
            toast.error(`Row ${i + 1}: Values cannot be negative`, messages.COLOR_RED);
            showLoading(false);
            return false;
          }

          const payload = {
            dsl_no: headerData.dslNo,
            survey_year: surveyYearOnly,
            item_code: (item.itemCode || '').toString().trim(),
            item_desc: (item.itemDesc || '').trim(),
            unit_of_qty: (item.unitOfQty || '').trim(),
            qty_consumed: qtyConsumed,
            purchase_value: purchaseValue
          };

          dataToSave.push(payload);
        }
      }

      if (dataToSave.length === 0) {
        toast.error('No data to save. Please fill at least one row.', messages.COLOR_RED);
        showLoading(false);
        return false;
      }

      // Prepare remarks array
      const remarksArray = [];
      if (remarksData.remarksBlock || remarksData.remarksItems || remarksData.remarks) {
        remarksArray.push({
          dslNo: headerData.dslNo,
          surveyYear: surveyYearOnly,
          block: 'I',
          columnsName: remarksData.remarksItems || '',
          Items: remarksData.remarksBlock || '',
          remarks: remarksData.remarks || ''
        });
      }

      // Structure payload with data and remarks arrays
      const finalPayload = {
        data: dataToSave,
        remarks: remarksArray
      };

      console.log('Saving data to API:', finalPayload);

      const response = await axios.post(`${API_URL}api/input-items-directlyItems/insertInputIItems`, finalPayload, currentConfig);

      // Check response
      if (response.data && response.data.status) {
        toast.success(`Successfully saved ${dataToSave.length} items to database`, messages.COLOR_RED);

        // Mark page as saved for green header
        markPageAsSaved(PAGE_NAMES.INPUT_ITEMS_IMPORTED);
        // Refresh balance sheet validation to update header colors
        if (headerData.despatchSlNo && headerData.surveyYear) {
          await refreshBalanceSheetValidation(headerData.despatchSlNo, headerData.surveyYear);
        }

        return true;
      } else {
        toast.error('Failed to save data. Please check the data and try again.', messages.COLOR_RED);
        return false;
      }

    } catch (error) {
      console.error('API Error Details:', error);
      console.error('Error Response:', error.response);
      console.error('Error Status:', error.response?.status);
      console.error('Error Data:', error.response?.data);

      if (error.response?.status === 403) {
        toast.error('Access denied (403). Please check your permissions or contact administrator.', messages.COLOR_RED);
      } else if (error.response?.status === 401) {
        toast.error('Unauthorized (401). Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        toast.error('API endpoint not found (404). Please check the URL.', messages.COLOR_RED);
      } else if (error.response?.status >= 500) {
        toast.error('Server error. Please try again later.', messages.COLOR_RED);
      } else {
        toast.error(`Error ${error.response?.status || 'Unknown'}: ${error.message || 'An error occurred'}`, messages.COLOR_RED);
      }
      return false;
    } finally {
      showLoading(false);
    }
  };

  // Filter remarks list using centralized function from Custom.js
  const filteredRemarksList = useMemo(() => {
    return filterRemarksList(remarksList, remarksData);
  }, [remarksList, remarksData]);

  return (
    <>
      <div className="container-fluid py-3">
        {/* Header Section */}
        <HeaderInfo
          title="Input Items - directly imported items only (consumed)"
          icon="fa fa-ship"
          iconColor="#1976d2"
          titleColor="#1976d2"
          backgroundColor=""
          dispatchSlNo={headerData.despatchSlNo}
          permanentSlNo={headerData.permanentSlNo}
          companyName={headerData.companyName}
          companyPlaceholder="Enter Company Name"
          showBlockCType={false}
          includeTableStyles={false}
        />

        {/* Main Content Area */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-12">
                {/* <h6 className="fw-semibold">
                  DSL No: <span className="fw-normal me-5">{headerData.despatchSlNo}</span>
                  Survey Year: <span className="fw-normal">2023-24</span>
                </h6> */}
                <div className="table-responsive asi_block_table">
                  <table cellPadding="0" cellSpacing="0" className="table table-bordered block_c_table" id="velblockh" ref={dtableRef}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: 'center' }}>Sl. No.</th>
                        <th style={{ textAlign: 'center' }}>Item Description</th>
                        <th style={{ textAlign: 'center' }}>NPC-MS</th>
                        <th style={{ textAlign: 'center' }}>Unit of Quantity</th>
                        <th style={{ textAlign: 'center' }}>Quantity Consumed</th>
                        <th style={{ textAlign: 'center' }}>Purchase Value (in Rs.)</th>
                        <th style={{ textAlign: 'center' }}>Rate Per Unit (in Rs.)</th>
                        <th style={{ textAlign: 'center' }}>Action</th>
                      </tr>
                      <tr>
                        <th style={{ textAlign: 'center' }}>(1)</th>
                        <th style={{ textAlign: 'center' }}>(2)</th>
                        <th style={{ textAlign: 'center' }}>(3)</th>
                        <th style={{ textAlign: 'center' }}>(4)</th>
                        <th style={{ textAlign: 'center' }}>(5)</th>
                        <th style={{ textAlign: 'center' }}>(6)</th>
                        <th style={{ textAlign: 'center' }}>(7)</th>
                        <th style={{ textAlign: 'center' }}>(8)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.values(itemData).slice(0, 8).map((item, i) => {

                        return (
                          <tr key={i}>
                            <td style={{ textAlign: 'center' }}>
                              {i + 1}
                            </td>
                            <td>
                              {i < 6 ? (
                                // Searchable dropdown for first 6 rows
                                <div className="position-relative">
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={itemData[item.id]?.itemDesc || ''}
                                    placeholder="Item Description"
                                    style={{ textAlign: 'center', paddingRight: '30px' }}
                                    onChange={(e) => {
                                      handleInputChange(item.id, 'itemDesc', e.target.value);
                                      handleSearchChange(e.target.value);
                                      // Reset selected index when typing
                                      setSelectedIndex(prev => ({ ...prev, [`${item.id}-itemDesc`]: -1 }));
                                    }}
                                    onFocus={(e) => {
                                      // Open dropdown on focus if it's closed (for keyboard navigation)
                                      // Use setTimeout to avoid conflict with mousedown
                                      setTimeout(() => {
                                        if (!dropdownStates[`${item.id}-itemDesc`]) {
                                          openDropdown(item.id, 'itemDesc');
                                        }
                                      }, 0);
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const key = `${item.id}-itemDesc`;
                                      // Only toggle if dropdown wasn't just opened by mousedown
                                      if (!justOpenedRef.current[key]) {
                                        toggleDropdown(item.id, 'itemDesc');
                                      } else {
                                        // Clear the flag since we handled it
                                        justOpenedRef.current[key] = false;
                                      }
                                    }}
                                    onMouseDown={(e) => {
                                      // Open dropdown immediately on mousedown to ensure it opens on first click
                                      // This prevents the need for double-click
                                      if (!dropdownStates[`${item.id}-itemDesc`]) {
                                        openDropdown(item.id, 'itemDesc');
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      handleKeyDown(e, item.id, 'itemDesc', getFilteredItemsForRow(item.id, filteredItems));
                                    }}
                                  />
                                  <i className="fa fa-chevron-down position-absolute"
                                    style={{
                                      right: '10px',
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      color: '#666',
                                      pointerEvents: 'none'
                                    }}></i>
                                  {dropdownStates[`${item.id}-itemDesc`] && (
                                    <div
                                      className="position-absolute w-100 bg-white border rounded shadow-sm"
                                      style={{
                                        top: '100%',
                                        left: 0,
                                        zIndex: 1000,
                                        maxHeight: '200px',
                                        overflowY: 'auto'
                                      }}
                                    >
                                      {(() => {
                                        // Filter out already selected items for this row
                                        const availableItems = getFilteredItemsForRow(item.id, filteredItems);
                                        return availableItems.length > 0 ? (
                                          <>
                                            {availableItems.map((singleItem, index) => {
                                              const key = `${item.id}-itemDesc`;
                                              const isSelected = selectedIndex[key] === index;
                                              return (
                                                <div
                                                  key={index}
                                                  ref={(el) => {
                                                    if (el) {
                                                      dropdownItemRefs.current[`${key}-${index}`] = el;
                                                    }
                                                  }}
                                                  className="px-3 py-2 cursor-pointer hover-bg-light"
                                                  style={{
                                                    cursor: 'pointer',
                                                    backgroundColor: isSelected ? '#e3f2fd' : 'white',
                                                    outline: isSelected ? '2px solid #1976d2' : 'none',
                                                    outlineOffset: '-2px'
                                                  }}
                                                  onClick={() => {
                                                    handleItemDescriptionChange(item.id, singleItem.ITEM_NAME);
                                                  }}
                                                  onMouseEnter={(e) => {
                                                    e.target.style.backgroundColor = '#f8f9fa';
                                                    setSelectedIndex(prev => ({ ...prev, [key]: index }));
                                                  }}
                                                  onMouseLeave={(e) => {
                                                    if (!isSelected) {
                                                      e.target.style.backgroundColor = 'white';
                                                    }
                                                  }}
                                                  tabIndex={-1}
                                                >
                                                  {singleItem.ITEM_NAME}
                                                </div>
                                              );
                                            })}
                                            {hasMoreData && (
                                              <div
                                                className="px-3 py-2 text-center text-muted"
                                                onClick={loadMoreData}
                                                style={{ cursor: 'pointer' }}
                                              >
                                                {isLoadingMore ? 'Loading more...' : 'Load more items'}
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          <div className="px-3 py-2 text-muted">
                                            {loadingItemMaster ? 'Loading items...' : 'No items found'}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                // Static text for special rows
                                <input
                                  type="text"
                                  className="form-control"
                                  value={i === 6 ? (itemData[item.id]?.itemDesc !== undefined ? itemData[item.id].itemDesc : 'Other Imported Items') : i === 7 ? "Total Imports (consumed) (items 1 to 6)" : ""}
                                  disabled
                                />
                              )}
                            </td>
                            <td className={`${itemData[item.id]?.itemCodeClass || ''}`}>
                              {i < 6 ? (
                                // Searchable dropdown for first 6 rows
                                <div className="position-relative">
                                  <input
                                    type="text"
                                    className={`form-control ${validationErrors[`${item.id}-itemCode`] ? 'is-invalid' : ''}`}
                                    value={itemData[item.id]?.itemCode || ''}
                                    placeholder="Item Code (7 digits)"
                                    style={{ textAlign: 'center', paddingRight: '30px' }}
                                    onChange={(e) => {
                                      handleInputChange(item.id, 'itemCode', e.target.value);
                                      handleSearchChange(e.target.value);
                                      // Reset selected index when typing
                                      setSelectedIndex(prev => ({ ...prev, [`${item.id}-itemCode`]: -1 }));
                                    }}
                                    onFocus={(e) => {
                                      // Open dropdown on focus if it's closed (for keyboard navigation)
                                      // Use setTimeout to avoid conflict with mousedown
                                      setTimeout(() => {
                                        if (!dropdownStates[`${item.id}-itemCode`]) {
                                          openDropdown(item.id, 'itemCode');
                                        }
                                      }, 0);
                                    }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const key = `${item.id}-itemCode`;
                                      // Only toggle if dropdown wasn't just opened by mousedown
                                      if (!justOpenedRef.current[key]) {
                                        toggleDropdown(item.id, 'itemCode');
                                      } else {
                                        // Clear the flag since we handled it
                                        justOpenedRef.current[key] = false;
                                      }
                                    }}
                                    onMouseDown={(e) => {
                                      // Open dropdown immediately on mousedown to ensure it opens on first click
                                      // This prevents the need for double-click
                                      if (!dropdownStates[`${item.id}-itemCode`]) {
                                        openDropdown(item.id, 'itemCode');
                                      }
                                    }}
                                    onKeyDown={(e) => {
                                      handleKeyDown(e, item.id, 'itemCode', getFilteredItemsForRow(item.id, filteredItems));
                                    }}
                                    onBlur={(e) => {
                                      const value = e.target.value;
                                      if (value && value.length > 0 && value.length < 7) {
                                        toast.warning('Item Code must be exactly 7 digits', messages.COLOR_RED);
                                      }
                                    }}
                                  />
                                  {validationErrors[`${item.id}-itemCode`] && (
                                    <div className="invalid-feedback" style={{ display: 'block', fontSize: '0.75rem', position: 'absolute', zIndex: 1001 }}>
                                      {validationErrors[`${item.id}-itemCode`]}
                                    </div>
                                  )}
                                  <i className="fa fa-chevron-down position-absolute"
                                    style={{
                                      right: '10px',
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      color: '#666',
                                      pointerEvents: 'none'
                                    }}></i>
                                  {dropdownStates[`${item.id}-itemCode`] && (
                                    <div
                                      className="position-absolute w-100 bg-white border rounded shadow-sm"
                                      style={{
                                        top: '100%',
                                        left: 0,
                                        zIndex: 1000,
                                        maxHeight: '200px',
                                        overflowY: 'auto'
                                      }}
                                    >
                                      {(() => {
                                        // Filter out already selected items for this row
                                        const availableItems = getFilteredItemsForRow(item.id, filteredItems);
                                        return availableItems.length > 0 ? (
                                          <>
                                            {availableItems.map((singleItem, index) => {
                                              const key = `${item.id}-itemCode`;
                                              const isSelected = selectedIndex[key] === index;
                                              return (
                                                <div
                                                  key={index}
                                                  ref={(el) => {
                                                    if (el) {
                                                      dropdownItemRefs.current[`${key}-${index}`] = el;
                                                    }
                                                  }}
                                                  className="px-3 py-2 cursor-pointer hover-bg-light"
                                                  style={{
                                                    cursor: 'pointer',
                                                    backgroundColor: isSelected ? '#e3f2fd' : 'white',
                                                    outline: isSelected ? '2px solid #1976d2' : 'none',
                                                    outlineOffset: '-2px'
                                                  }}
                                                  onClick={() => {
                                                    handleItemCodeChange(item.id, singleItem.ASICC_CODE);
                                                  }}
                                                  onMouseEnter={(e) => {
                                                    e.target.style.backgroundColor = '#f8f9fa';
                                                    setSelectedIndex(prev => ({ ...prev, [key]: index }));
                                                  }}
                                                  onMouseLeave={(e) => {
                                                    if (!isSelected) {
                                                      e.target.style.backgroundColor = 'white';
                                                    }
                                                  }}
                                                  tabIndex={-1}
                                                >
                                                  {singleItem.ASICC_CODE}
                                                </div>
                                              );
                                            })}
                                            {hasMoreData && (
                                              <div
                                                className="px-3 py-2 text-center text-muted"
                                                onClick={loadMoreData}
                                                style={{ cursor: 'pointer' }}
                                              >
                                                {isLoadingMore ? 'Loading more...' : 'Load more items'}
                                              </div>
                                            )}
                                          </>
                                        ) : (
                                          <div className="px-3 py-2 text-muted">
                                            {loadingItemMaster ? 'Loading items...' : 'No items found'}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                // Static text for special rows: row 7 = 9922100, row 8 = 9994000
                                <input
                                  type="text"
                                  className="form-control"
                                  value={i === 6 ? "9922100" : i === 7 ? "9994000" : ""}
                                  disabled
                                />
                              )}
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={itemData[item.id]?.unitOfQty || ''}
                                id={`unitOfQty${i}`}
                                name={`unitOfQty${i}`}
                                maxLength="19"
                                disabled
                                onChange={e => handleInputChange(item.id, 'unitOfQty', e.target.value)}
                              />
                            </td>
                            <td>
                              <div className="position-relative">
                                <input
                                  type="text"
                                  className={`form-control ${validationErrors[`${item.id}-qtyConsumed`] ? 'is-invalid' : ''} ${i === 7 ? 'text-bold-inside-textbox' : ''}`}
                                  value={i === 7 ? totalImportsQtyConsumed : (itemData[item.id]?.qtyConsumed || '')}
                                  id={`qtyConsumed${i}`}
                                  name={`qtyConsumed${i}`}
                                  maxLength="18"
                                  disabled={i === 6 || i === 7}
                                  readOnly={i === 7}
                                  onChange={e => handleInputChange(item.id, 'qtyConsumed', e.target.value)}
                                  onBlur={(e) => {
                                    const value = e.target.value;
                                    if (value && parseFloat(value) < 0) {
                                      handleInputChange(item.id, 'qtyConsumed', '0');
                                      toast.warning('Quantity cannot be negative', messages.COLOR_RED);
                                    }
                                  }}
                                />
                                {validationErrors[`${item.id}-qtyConsumed`] && (
                                  <div className="invalid-feedback" style={{ display: 'block', fontSize: '0.75rem' }}>
                                    {validationErrors[`${item.id}-qtyConsumed`]}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="position-relative">
                                <input
                                  type="text"
                                  className={`form-control ${validationErrors[`${item.id}-purchaseValue`] ? 'is-invalid' : ''} ${i === 7 ? 'text-bold-inside-textbox' : ''}`}
                                  value={i === 7 ? totalImportsPurchaseValue : (itemData[item.id]?.purchaseValue || '')}
                                  id={`purchaseValue${i}`}
                                  name={`purchaseValue${i}`}
                                  maxLength="12"
                                  disabled={i === 7}
                                  readOnly={i === 7}
                                  onChange={e => handleInputChange(item.id, 'purchaseValue', e.target.value)}
                                  onBlur={(e) => {
                                    const value = e.target.value;
                                    if (value && parseFloat(value) < 0) {
                                      handleInputChange(item.id, 'purchaseValue', '0');
                                      toast.warning('Purchase Value cannot be negative', messages.COLOR_RED);
                                    }
                                  }}
                                />
                                {validationErrors[`${item.id}-purchaseValue`] && (
                                  <div className="invalid-feedback" style={{ display: 'block', fontSize: '0.75rem' }}>
                                    {validationErrors[`${item.id}-purchaseValue`]}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <input
                                type="text"
                                className="form-control"
                                value={(() => {
                                  const pv = i === 7 ? totalImportsPurchaseValue : itemData[item.id]?.purchaseValue;
                                  const qty = i === 7 ? totalImportsQtyConsumed : itemData[item.id]?.qtyConsumed;
                                  const pvNum = pv !== '' && pv != null ? parseFloat(pv) : NaN;
                                  const qtyNum = qty !== '' && qty != null ? parseFloat(qty) : NaN;
                                  if (Number.isFinite(pvNum) && Number.isFinite(qtyNum) && qtyNum > 0) {
                                    return (pvNum / qtyNum).toFixed(2);
                                  }
                                  return itemData[item.id]?.ratePerUnit ?? '';
                                })()}
                                readOnly
                                disabled
                                style={{ backgroundColor: '#e9ecef', cursor: 'not-allowed' }}
                              />
                            </td>
                            <td>
                              {i < 6 && (
                                <a
                                  href="#"
                                  className='d-block text-center'
                                  onClick={(e) => {
                                    e.preventDefault();
                                    handleDeleteSingleRow(i);
                                  }}
                                  title={`Delete Row ${i + 1}`}
                                >
                                  <i className="fa fa-trash"></i>
                                </a>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='card mb-3'>
          <div className='card-header'><h5 className='mb-0 pb-1 pt-1' style={{ fontWeight: 'bold' }}>Control Figures from Profit and Loss</h5></div>
          <div className='card-body'>
            <table className='table table-bordered' style={{ minWidth: '100%' }}>
              <thead>
                <tr>
                  <th>SL. No.</th>
                  <th>Description</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {totalProfitandLossAmount.SL_No !== '' || totalProfitandLossAmount.Description !== '' || totalProfitandLossAmount.Amount !== '' ? (
                  <tr>
                    <td>{totalProfitandLossAmount.SL_No !== '' ? totalProfitandLossAmount.SL_No : ''}</td>
                    <td>{totalProfitandLossAmount.Description || ''}</td>
                    <td>
                      <input
                        type="text"
                        maxLength={12}
                        className={`form-control ${profitLossAmountError ? 'is-invalid' : ''}`}
                        value={totalProfitandLossAmount.Amount ?? ''}
                        onChange={(e) => {
                          let val = (e.target.value || '').toString();
                          if (val !== '' && !/^\d*\.?\d*$/.test(val)) return;
                          if (val.length > 12) val = val.substring(0, 12);
                          setProfitLossAmountDirty(true);
                          setProfitLossAmountError('');
                          setTotalProfitandLossAmount(prev => ({ ...prev, Amount: val }));
                        }}
                        style={{ textAlign: 'center' }}
                      />
                      {profitLossAmountError && (
                        <div className="invalid-feedback" style={{ display: 'block', fontSize: '0.75rem' }}>
                          {profitLossAmountError}
                        </div>
                      )}
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan={3} style={{ textAlign: 'center' }}>
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

        </div>
        {/* Remarks Section */}
        <div className='row'>
          <div className='col-md-6 remarks-section'>

            <Remarks
              remarksData={remarksData}
              setRemarksData={setRemarksData}
              blockItemData={[]}
              filteredBlockItems={[]}
              isLoadingBlockItems={false}
              debouncedBlockItemSearch={() => { }}
              loadMoreBlockItemData={() => { }}
              hasMoreBlockItems={false}
              isLoadingMoreBlockItems={false}
              selectedItemsFromTable={selectedItemDescriptions}
              remarksList={remarksList}
              availableColumns={[5, 6]}
            />
          </div>
          <div className='col-md-6'>
            <div className='card'>
              <div className='card-body'>
                <h6 className='mb-3' style={{ fontWeight: 'bold', color: '#f56565' }}><i className='fa fa-exclamation-triangle me-2'></i>Note</h6>
                <ul className='list-styled' style={{ fontSize: '14px', color: '#f56565' }}>
                  <li>For the 'Others' item, Columns 3 and Blocks are also Mandatory</li>
                </ul>
              </div>
            </div>
          </div>


        </div>
        {/* Action Buttons */}
        <div className="row">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button
              type="button"
              className="btn btn_orange mx-1"
            >
              Print
              <i className="fa fa-print ms-1"></i>
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={handleSaveToAPI}
            >
              Save
            </button>
            <button
              type="reset"
              className="btn btn_orange mx-1"
              onClick={handleReset}
            >
              Reset
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/asiScheduleMainMenu')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/input-items')}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={async () => {
                // Save data first, then navigate only if successful
                const saveSuccess = await handleSaveToAPI();
                if (saveSuccess) {
                  navigate('/products-and-by-products');
                }
                // If save fails, stay on current page
              }}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div>
        <div className=''>  <Remarks__list remarksData={filteredRemarksList} /> </div>
      </div>
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />
    </>
  );
}

export default InputItemsImported; 